import { useState, useEffect } from "react";
import SEO from "../seo";
import {
  APP_DATA,
  PLANS_PRICING,
  TRANSACTION_FEES,
  STAFF_ACCOUNTS,
} from "../constant_data";
const StarterPlan = () => {
  const [activeSection, setActiveSection] = useState("pricing");

  useEffect(() => {
    const handleScroll = () => {
      const sections = ["pricing", "features", "perfect-for"];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="xPage Starter Plan – Launch Your Store with xPage"
        description="Get started with xPage Starter Plan. Affordable pricing, core eCommerce tools, AI landing pages, and essential features to launch your online store quickly."
        keywords="xPage Starter Plan, eCommerce starter plan, landing page builder, AI tools, online store builder"
        canonical="https://help.xpage.ai/help/pricing-plans/Starter%20Plan"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Starter Plan
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            The most affordable entry point to launch your online landing page
            with essential eCommerce tools. It's designed for small businesses,
            new entrepreneurs, and anyone looking to start selling online with
            low overhead and core functionality.
          </p>
        </div>

        {/* Pricing Details */}
        <div id="pricing" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Pricing
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-tag text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                ${PLANS_PRICING.STARTER.MONTHLY}
              </h3>
              <p className="text-systemLabel text-sm">per month</p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-credit-card text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                Card Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.STARTER.CARD}.00% fee
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-money-bill-wave text-xl text-core mb-2"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-1">
                COD Processing
              </h3>
              <p className="text-systemLabel text-sm">
                {TRANSACTION_FEES.STARTER.COD}.00% fee
              </p>
            </div>
          </div>
        </div>

        {/* Features Table */}
        <div id="features" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Everything Included
          </h2>
          <div className="bg-systemBackground border border-systemBorder rounded-lg overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-400/10 border-b border-systemBorder">
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Feature Category
                  </th>
                  <th className="text-left p-3 text-systemTitle text-sm font-semibold">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Core Features
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    AI-powered online landing page builder, unlimited AI landing
                    pages, mobile-first design, custom domain, 24/7 support
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Staff Accounts
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    {STAFF_ACCOUNTS.STARTER} staff member accounts to help
                    manage your landing page
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Payment & Checkout
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multiple gateways (Stripe, Paypal, Bancontact, COD & more),
                    high-converting checkout, transparent processing fees (
                    {TRANSACTION_FEES.STARTER.CARD}% card rate,{" "}
                    {TRANSACTION_FEES.STARTER.COD}% COD rate)
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Analytics
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Advanced analytics and real-time reports to track sales and
                    customer behavior
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Marketing Tools
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Abandoned checkout recovery, discount codes, product
                    bundles, referral & affiliate program, unlimited contacts,
                    customer segmentation
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    International Selling
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Multi-language support, duties & import taxes calculation
                  </td>
                </tr>
                <tr className="border-b border-systemBorder hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Hosting & Security
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Unlimited web hosting, free SSL certificate, secure
                    PCI-compliant payment processing
                  </td>
                </tr>
                <tr className="hover:bg-gray-400/5">
                  <td className="p-3 font-semibold text-systemTitle text-sm">
                    Threshold System
                  </td>
                  <td className="p-3 text-systemLabel text-sm">
                    Sales-based billing starting at a $15 threshold, increasing
                    dynamically as you grow
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Who It's For Section */}
        <div id="perfect-for" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">
            Perfect For
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-store text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Small Sellers
              </h3>
              <p className="text-systemLabel text-sm">
                Starting small and want affordable access to core eCommerce
                tools without overwhelming features.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-flask text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Entrepreneurs
              </h3>
              <p className="text-systemLabel text-sm">
                Testing a new product or market before scaling up with minimal
                financial commitment.
              </p>
            </div>
            <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 text-center">
              <i className="fas fa-tools text-2xl text-core mb-3"></i>
              <h3 className="text-lg font-semibold text-systemTitle mb-2">
                Essential Users
              </h3>
              <p className="text-systemLabel text-sm">
                Businesses that need essential features without paying for
                higher-tier automation or larger teams.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#pricing"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "pricing"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Pricing
              </a>
            </li>
            <li>
              <a
                href="#features"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "features"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Everything Included
              </a>
            </li>
            <li>
              <a
                href="#perfect-for"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "perfect-for"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Perfect For
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default StarterPlan;
